import "./image-node-view.css"
import styles from "./image-node-view.module.css"

import { NodeSelection } from "@tiptap/pm/state"
import type { Editor, ReactNodeViewProps } from "@tiptap/react"
import { useCallback, useEffect, useRef, useState } from "react"

import { DraggableNodeViewWrapper } from "@shared/unified-tiptap/editor-ui/drag-handle/DraggableNodeViewWrapper"

export interface ResizeParams {
  handleUsed: "left" | "right"
  initialWidth: number
  initialClientX: number
}

export interface ResizableImageProps extends React.HTMLAttributes<HTMLDivElement> {
  src: string
  alt?: string
  uploadId?: number | null
  editor?: Editor
  minWidth?: number
  maxWidth?: number
  initialWidth: number | null
  onImageResize?: (width: number | null) => void
  getPos: () => number | undefined
  props: ReactNodeViewProps
}

export function ImageNodeView(props: ReactNodeViewProps) {
  const { editor, node, updateAttributes, getPos } = props

  return (
    <ResizableImage
      src={node.attrs.src}
      alt={node.attrs.alt || ""}
      uploadId={node.attrs.uploadId}
      editor={editor}
      initialWidth={node.attrs.width}
      onImageResize={(width) => updateAttributes({ width })}
      getPos={getPos}
      props={props}
    />
  )
}

export const ResizableImage: React.FC<ResizableImageProps> = ({
  src,
  alt = "",
  uploadId,
  editor,
  minWidth = 96,
  maxWidth = 800,
  initialWidth,
  onImageResize,
  getPos,
  props,
}) => {
  const [resizeParams, setResizeParams] = useState<ResizeParams | undefined>()
  const [width, setWidth] = useState<number | null>(initialWidth)
  const [showHandles, setShowHandles] = useState(false)
  const isResizingRef = useRef(false)
  const isMountedRef = useRef(true)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const leftResizeHandleRef = useRef<HTMLDivElement>(null)
  const rightResizeHandleRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // The node is a leaf, so a click inside it doesn't produce a selection on its own.
  const handleImageClick = useCallback(
    (event: React.MouseEvent) => {
      if (!editor || !getPos || resizeParams) return

      event.preventDefault()
      event.stopPropagation()

      const pos = getPos()
      if (isValidPosition(pos)) {
        editor.chain().focus().setNodeSelection(pos).run()
      }
    },
    [editor, getPos, resizeParams],
  )

  const windowMouseMoveHandler = useCallback(
    (event: MouseEvent | TouchEvent): void => {
      if (!resizeParams || !editor || !isMountedRef.current) return

      const clientX = "touches" in event ? (event.touches[0]?.clientX ?? 0) : event.clientX
      const isLeftHandle = resizeParams.handleUsed === "left"
      const delta = isLeftHandle
        ? resizeParams.initialClientX - clientX
        : clientX - resizeParams.initialClientX

      const newWidth = resizeParams.initialWidth + delta
      const effectiveMaxWidth = editor.view.dom?.firstElementChild?.clientWidth || maxWidth
      const clampedWidth = Math.min(Math.max(newWidth, minWidth), effectiveMaxWidth)

      setWidth(clampedWidth)
      if (wrapperRef.current) {
        wrapperRef.current.style.width = `${clampedWidth}px`
      }
    },
    [editor, maxWidth, minWidth, resizeParams],
  )

  const windowMouseUpHandler = useCallback(
    (event: MouseEvent | TouchEvent): void => {
      if (!editor || !isMountedRef.current) return

      const target =
        "touches" in event
          ? document.elementFromPoint(
              event.changedTouches[0]?.clientX ?? 0,
              event.changedTouches[0]?.clientY ?? 0,
            )
          : event.target

      const isInsideWrapper = target && wrapperRef.current?.contains(target as Node)

      if ((!isInsideWrapper || !editor.isEditable) && showHandles && isMountedRef.current) {
        setShowHandles(false)
      }

      if (!resizeParams) return

      const wasNodeSelection =
        editor.state.selection instanceof NodeSelection &&
        editor.state.selection.node.type.name === "image"

      if (isMountedRef.current) {
        setResizeParams(undefined)
      }
      onImageResize?.(width)

      // Resizing drops the selection, so restore it afterwards.
      const pos = getPos()

      if (isValidPosition(pos) && wasNodeSelection && isMountedRef.current) {
        editor.chain().focus().setNodeSelection(pos).run()
      }

      isResizingRef.current = false
    },
    [editor, getPos, onImageResize, resizeParams, showHandles, width],
  )

  const startResize = useCallback(
    (handleUsed: "left" | "right", clientX: number) => {
      setResizeParams({
        handleUsed,
        initialWidth: wrapperRef.current?.clientWidth ?? minWidth,
        initialClientX: clientX,
      })
      isResizingRef.current = true
    },
    [minWidth],
  )

  const leftResizeHandleMouseDownHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    startResize("left", event.clientX)
  }

  const leftResizeHandleTouchStartHandler = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault()
    const touch = event.touches[0]
    if (touch) startResize("left", touch.clientX)
  }

  const rightResizeHandleMouseDownHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    startResize("right", event.clientX)
  }

  const rightResizeHandleTouchStartHandler = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault()
    const touch = event.touches[0]
    if (touch) startResize("right", touch.clientX)
  }

  const wrapperMouseEnterHandler = () => {
    if (editor?.isEditable && isMountedRef.current) setShowHandles(true)
  }

  const wrapperMouseLeaveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isMountedRef.current) return

    if (
      event.relatedTarget === leftResizeHandleRef.current ||
      event.relatedTarget === rightResizeHandleRef.current ||
      resizeParams
    )
      return

    if (editor?.isEditable) setShowHandles(false)
  }

  const wrapperTouchStartHandler = () => {
    if (editor?.isEditable && isMountedRef.current) setShowHandles(true)
  }

  useEffect(() => {
    window.addEventListener("mousemove", windowMouseMoveHandler)
    window.addEventListener("mouseup", windowMouseUpHandler)
    window.addEventListener("touchmove", windowMouseMoveHandler, {
      passive: false,
    })
    window.addEventListener("touchend", windowMouseUpHandler)

    return () => {
      window.removeEventListener("mousemove", windowMouseMoveHandler)
      window.removeEventListener("mouseup", windowMouseUpHandler)
      window.removeEventListener("touchmove", windowMouseMoveHandler)
      window.removeEventListener("touchend", windowMouseUpHandler)
    }
  }, [windowMouseMoveHandler, windowMouseUpHandler])

  useEffect(() => {
    // Cp mod: Add the next line due to https://github.com/ueberdosis/tiptap-ui-components/issues/37
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Cp mod: Add drag handle.
  return (
    <DraggableNodeViewWrapper handleClassName={styles.imageDragHandle} {...props}>
      <div
        onMouseEnter={wrapperMouseEnterHandler}
        onMouseLeave={wrapperMouseLeaveHandler}
        onTouchStart={wrapperTouchStartHandler}
        data-width={width}
        className="tiptap-image"
        // This is needed in Firefox to prevent dragging the image.
        // (Chrome is fine just with Tiptap UI's draggable={false} below.)
        onDragStart={(e) => e.preventDefault()}
      >
        <div
          ref={wrapperRef}
          className="tiptap-image-container"
          style={{ width: width ? `${width}px` : "fit-content" }}
          // The wrapper is full editor width while the image is fit-content; size the
          // drag preview card to the image, not the wrapper (see DragPreview.ts).
          data-drag-preview-size
        >
          <div className="tiptap-image-content">
            {uploadId != null ? (
              <div className="tiptap-image-uploading" contentEditable={false}>
                Uploading...
              </div>
            ) : (
              <img
                ref={imageRef}
                src={src}
                alt={alt}
                className="tiptap-image-img"
                contentEditable={false}
                draggable={false}
                onClick={handleImageClick}
                // Cp mod: Keep as default cursor, since you can't drag.
                // style={{ cursor: editor?.isEditable ? "pointer" : "default" }}
              />
            )}

            {showHandles && editor?.isEditable && (
              <>
                <div
                  ref={leftResizeHandleRef}
                  className="tiptap-image-handle tiptap-image-handle-left"
                  onMouseDown={leftResizeHandleMouseDownHandler}
                  onTouchStart={leftResizeHandleTouchStartHandler}
                />
                <div
                  ref={rightResizeHandleRef}
                  className="tiptap-image-handle tiptap-image-handle-right"
                  onMouseDown={rightResizeHandleMouseDownHandler}
                  onTouchStart={rightResizeHandleTouchStartHandler}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </DraggableNodeViewWrapper>
  )
}

// Cp mod: Copied here from Tiptap UI's lib/tiptap-utils.ts.
/**
 * Checks if a value is a valid number (not null, undefined, or NaN)
 * @param value - The value to check
 * @returns boolean indicating if the value is a valid number
 */
function isValidPosition(pos: number | null | undefined): pos is number {
  return typeof pos === "number" && pos >= 0
}
